<script lang="ts" setup>
const props = defineProps({
  attribute: {
    default: '',
    type: String,
  },
  companiesCount: {
    default: 0,
    type: Number,
  },
  icon: {
    default: '',
    type: String,
  },
  label: {
    default: '',
    type: String,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  value: {
    default: '',
    type: String,
  },
  values: {
    default: () => [],
    type: Array as PropType<SegmentationValues>,
  },
})

const emit = defineEmits<{
  toggle: [value: string]
}>()

const { t } = useI18n()

const avatar = computed(() => {
  if (props.attribute === 'technologies.active' && !props.value.endsWith('-category')) {
    return getLogo(props.value, 'technologies')
  }

  return null
})

const isAdded = computed(() => {
  return props.values.includes(props.value)
})

function onToggle() {
  emit('toggle', props.value)
}
</script>

<template>
  <div
    class="flex w-full items-center rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm dark:border-gray-700"
    :class="{
      'bg-gray-100 dark:bg-gray-800': !loading,
      'bg-white dark:bg-gray-900': loading || isAdded,
    }"
  >
    <template v-if="!loading">
      <div class="mr-4 flex flex-1 select-none items-start text-sm">
        <UAvatar
          v-if="avatar"
          class="flex-initial flex-shrink-0"
          size="xs"
          :src="avatar"
        />

        <UIcon
          v-else-if="icon"
          class="size-5  flex-initial flex-shrink-0"
          :name="icon"
        />

        <span class="ml-2 flex-1 text-xs uppercase">
          {{ label }}

          <UTooltip
            v-if="companiesCount"
            :popper="{ placement: 'top' }"
            :text="t('tooltip', companiesCount)"
          >
            <span class="ml-0.5 text-gray-600 dark:text-gray-400">
              ({{ numbro(companiesCount) }})
            </span>
          </UTooltip>
        </span>
      </div>

      <UButton
        class="flex-initial"
        :icon="isAdded ? getIcon('check') : getIcon('add')"
        :label="isAdded ? t('globals.added') : t('globals.add')"
        size="2xs"
        :ui="{ icon: { base: isAdded ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400' } }"
        @click="onToggle"
      />
    </template>

    <div
      v-else
      class="flex h-5 w-full items-center space-x-2"
    >
      <USkeleton
        class="size-5 flex-initial"
        :ui="{ rounded: 'rounded-full' }"
      />

      <USkeleton class="h-5 flex-1" />

      <USkeleton class="h-5 w-[60px] flex-initial" />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "tooltip": "{count} company | {count} companies"
  },
  "es": {
    "tooltip": "{count} empresa | {count} empresas"
  },
  "fr": {
    "tooltip": "{count} entreprise | {count} entreprises"
  }
}
</i18n>
